import React, { useState, useEffect, useCallback } from "react";
import { buildImageUrl } from "@/lib/api";
import { ImageOff, X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Image lightbox + thumbnail components.
 *
 * <ImageThumb path={storagePath} name="..." onClick={() => openPreview(images, 0)} />
 * <ImagePreview images={[{storage_path, name}, ...]} initialIndex={0} onClose={...} />
 */

export function ImageThumb({ path, name, size = 40, className = "", onClick, testId }) {
  const s = typeof size === "number" ? `${size}px` : size;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-md bg-gradient-to-br from-amber-50 to-red-50 overflow-hidden flex items-center justify-center border border-gray-200 shrink-0 hover:ring-2 hover:ring-[#9B1C1C]/30 hover:border-[#9B1C1C] transition-all ${onClick ? "cursor-zoom-in" : ""} ${className}`}
      style={{ width: s, height: s }}
      title={onClick ? `Preview ${name || "image"}` : undefined}
      aria-label={onClick ? `Preview ${name || "image"}` : undefined}
    >
      {path ? (
        <img src={buildImageUrl(path)} alt={name || ""} className="w-full h-full object-cover" />
      ) : (
        <ImageOff className="h-4 w-4 text-gray-300" />
      )}
    </button>
  );
}

export function ImagePreview({ images, initialIndex = 0, onClose }) {
  const [idx, setIdx] = useState(initialIndex);

  const go = useCallback((delta) => {
    setIdx((i) => (i + delta + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [go, onClose]);

  if (!images || images.length === 0) return null;
  const current = images[idx];
  if (!current) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="image-preview-overlay"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="image-preview-close"
        aria-label="Close preview"
      >
        <X className="h-5 w-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Next image"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <div className="max-w-5xl max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={buildImageUrl(current.storage_path)}
          alt={current.name || ""}
          className="max-w-full max-h-[80vh] object-contain rounded-md shadow-2xl"
        />
        {current.name && (
          <p className="text-white/90 text-sm mt-4 font-medium">{current.name}</p>
        )}
        {images.length > 1 && (
          <p className="text-white/60 text-xs mt-1">{idx + 1} of {images.length}</p>
        )}
      </div>
    </div>
  );
}

/** Convenience hook to wire up preview state. */
export function useImagePreview() {
  const [state, setState] = useState({ images: [], index: 0, open: false });
  const open = (images, index = 0) => setState({ images, index, open: true });
  const close = () => setState((s) => ({ ...s, open: false }));
  return { state, open, close };
}
